import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Box,
  MenuItem,
  Grid,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import userService from '../../services/userService';
import LayoutContainer from '../../components/LayoutContainer';
import AdminHeader from '../../components/AdminHeader';
import { find, isEmpty, join, map, omit, toUpper } from 'lodash';
import locationService from '../../services/locationService';
import companyService from '../../services/companyService';
import roleService from '../../services/roleService';
import departmentService from '../../services/departmentService';
import fileUploadService from '../../services/fileUploadService';
import functionalDepartmentService from '../../services/functionalDepartmentService';
import volunteerService from '../../services/volunteerService';

const VolunteerForm = () => {
  // src/pages/users/UserForm.js

  const { id } = useParams();
  const navigate = useNavigate();
  const [vendorList, setVendorList] = useState([]);
  const [reportingHeadList, setReportingHeadList] = useState([]);
  const [vendorDepts, setVendorDepts] = useState([]);
  const [initialValues, setInitialValues] = useState({
    name: '',
    phone: '',
    email: '',
    dob: '',
    aadharno: '',
    aadharphoto: '',
    reporting_head: '',
    vendor_id: '',
    active: true,
  });

  const getVendorById = async (id) => {
    const response = await functionalDepartmentService.getVendorByReportingHead(
      id
    );
    setVendorDepts(response?.data?.[0]?.departments);
    setVendorList(response?.data?.[0]?.vendor);
  };

  const fetchVolunteer = async () => {
    if (id) {
      const response = await volunteerService.getVolunteerById(id);
      setInitialValues(response?.data);
      getVendorById(response?.data?.reporting_head);
    }
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(`Volunteers's name is required`),
    phone: Yup.string().required('Phone number is required'),
    aadharno: Yup.string().required('Last 4 digit of addhar is required'),
    email: Yup.string().required('Email is required'),
    dob: Yup.string().required('Date of birth is required'),
    reporting_head: Yup.string().required('Reporting head is required'),
    vendor_id: Yup.string().required('Vendor is required'),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      if (id) {
        await volunteerService.updateVolunteer(id, values);
      } else {
        await volunteerService.createVolunteer(values);
      }
      navigate('/volunteers');
    } catch (error) {
      console.error(error); // Handle error appropriately
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = async (setFieldValue, file) => {
    const res = await fileUploadService?.handleFileUpload(file);
    if (res?.status === 200) {
      setFieldValue(
        'aadharphoto',
        `${process.env.REACT_APP_IMAGE_URL}/images/${res?.data?.filename}`
      );
    }
  };

  useEffect(() => {
    const fetchReportingHead = async () => {
      const response = await functionalDepartmentService.getReportingHeads();
      setReportingHeadList(response.data?.data);
    };
    fetchReportingHead();

    if (id) {
      fetchVolunteer();
    }
  }, [id]);

  const menuBox = {
    PaperProps: {
      style: {
        maxHeight: 200,
        width: 160,
      },
    },
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    getContentAnchorEl: null,
  };

  return (
    <LayoutContainer>
      <AdminHeader
        title={id ? 'Edit Volunteer' : 'Add Volunteer'}
        backTo='/volunteers'
      />
      <Box
        className='page-content-wrapper scrollable'
        p={3}
        sx={{ display: 'flex', backgroundColor: '#efefef' }}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            handleChange,
            handleBlur,
            handleSubmit,
            values,
            errors,
            touched,
            setFieldValue,
          }) => (
            <Form onSubmit={handleSubmit} style={{ width: '50%' }}>
              <Card>
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        Reporting Head <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <TextField
                        select
                        name='reporting_head'
                        fullWidth
                        value={values.reporting_head || ''}
                        onChange={(e) => {
                          setFieldValue('reporting_head', e?.target?.value);
                          getVendorById(e?.target?.value);
                        }}
                        onBlur={handleBlur}
                        sx={{
                          borderRadius: 1,
                          backgroundColor: '#f5f5f5',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor:
                                touched['reporting_head'] &&
                                errors['reporting_head']
                                  ? '#d32f2f'
                                  : '#ccc',
                            },
                          },
                        }}
                        SelectProps={{
                          MenuProps: {
                            ...menuBox,
                          },
                        }}
                        error={
                          touched.reporting_head &&
                          Boolean(errors.reporting_head)
                        }
                        helperText={
                          touched.reporting_head && errors.reporting_head
                        }
                      >
                        {reportingHeadList?.map((reportingHead) => (
                          <MenuItem
                            key={reportingHead.id}
                            value={reportingHead.id}
                          >
                            {reportingHead.username}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        pt: '10px !important',
                      }}
                    >
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        Vendor <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <TextField
                        select
                        name='vendor_id'
                        fullWidth
                        value={values.vendor_id || ''}
                        onChange={(e) => {
                          setFieldValue('vendor_id', e?.target?.value);
                        }}
                        onBlur={handleBlur}
                        sx={{
                          borderRadius: 1,
                          backgroundColor: '#f5f5f5',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor:
                                touched['vendor_id'] && errors['vendor_id']
                                  ? '#d32f2f'
                                  : '#ccc',
                            },
                          },
                        }}
                        SelectProps={{
                          MenuProps: {
                            ...menuBox,
                          },
                        }}
                        error={touched.vendor_id && Boolean(errors.vendor_id)}
                        helperText={touched.vendor_id && errors.vendor_id}
                      >
                        {[vendorList]?.map((vendor) => (
                          <MenuItem key={vendor.id} value={vendor.id}>
                            {vendor.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    {!isEmpty(vendorDepts) && (
                      <Grid
                        item
                        xs={12}
                        sx={{
                          pt: '10px !important',
                        }}
                      >
                        <Box sx={{ borderBottom: '1px solid grey' }}>
                          <Typography
                            variant='body2'
                            fontWeight={600}
                            sx={{ pb: 1 }}
                          >
                            {join(vendorDepts, ', ')}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {[
                      { name: 'name', label: `Volunteer's Name` },
                      { name: 'phone', label: 'Mobile No.' },
                      { name: 'email', label: 'Email' },
                      { name: 'dob', label: 'Date of Birth' },
                    ].map(
                      (
                        {
                          name,
                          label,
                          type = name === 'dob' ? 'date' : 'text',
                        },
                        index
                      ) => (
                        <Grid
                          item
                          xs={12}
                          key={name}
                          sx={{ pt: index !== 0 && '10px !important' }}
                        >
                          <Typography variant='body2' sx={{ fontWeight: 600 }}>
                            {label}
                            <span style={{ color: 'red' }}>*</span>
                          </Typography>
                          <TextField
                            name={name}
                            type={type}
                            fullWidth
                            value={values[name]}
                            onChange={(e) => {
                              const capitalizedValue =
                                name === 'name'
                                  ? toUpper(e.target.value)
                                  : e.target.value; // Capitalize first letter
                              handleChange({
                                target: {
                                  name: name,
                                  value: capitalizedValue,
                                },
                              });
                            }}
                            onBlur={handleBlur}
                            error={touched[name] && Boolean(errors[name])}
                            helperText={touched[name] && errors[name]}
                            sx={{
                              borderRadius: 1,
                              backgroundColor: '#f5f5f5',
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  borderColor:
                                    touched[name] && errors[name]
                                      ? '#d32f2f'
                                      : '#ccc',
                                },
                              },
                            }}
                          />
                        </Grid>
                      )
                    )}
                    <Grid
                      item
                      xs={12}
                      sx={{
                        pt: '14px !important',
                      }}
                    >
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        Upload Aadhar Card :{' '}
                      </Typography>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                        }}
                      >
                        <input
                          type='file'
                          accept='image/*'
                          onChange={(e) => {
                            handleFileChange(setFieldValue, e.target.files[0]);
                          }}
                          style={{
                            width: values?.aadharphoto ? '85%' : '100%',
                            padding: '8px',
                            marginTop: '3px',
                            borderRadius: '4px',
                            backgroundColor: '#f5f5f5',
                            border: '1px solid #ccc',
                            // Adding a border color change on error state
                            borderColor:
                              touched?.aadharphoto && errors.aadharphoto
                                ? '#d32f2f'
                                : '#ccc',
                          }}
                        />
                        {values?.aadharphoto && (
                          <img
                            src={values?.aadharphoto}
                            width={50}
                            height={50}
                          />
                        )}
                      </div>
                    </Grid>
                    <Grid item xs={12} sx={{ pt: '10px !important' }}>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        Aadhar last 4 digits
                        <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <TextField
                        name='aadharno'
                        fullWidth
                        value={values?.aadharno}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched?.aadharno && Boolean(errors.aadharno)}
                        helperText={touched?.aadharno && errors.aadharno}
                        sx={{
                          borderRadius: 1,
                          backgroundColor: '#f5f5f5',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor:
                                touched.aadharno && errors.aadharno
                                  ? '#d32f2f'
                                  : '#ccc',
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        pt: '10px !important',
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.active}
                            color='primary'
                            name='active'
                            onChange={(e) =>
                              setFieldValue('active', e?.target?.checked)
                            }
                          />
                        }
                        label={
                          <Typography variant='body2' sx={{ fontWeight: 600 }}>
                            Active Volunteer
                          </Typography>
                        }
                      />
                    </Grid>
                  </Grid>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      type='submit'
                      variant='contained'
                      sx={{ backgroundColor: '#be210b', textTransform: 'none' }}
                    >
                      {id ? 'Update' : 'Create'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Form>
          )}
        </Formik>
      </Box>
    </LayoutContainer>
  );
};
export default VolunteerForm;
